import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import store from './store';
import { selectUser, selectIsAuth } from './store/slices/authSlice';
import { useQuery } from './hooks';
import { businessAPI } from './services/api';

// Layouts
import { AdminShell } from './components/layout/AdminShell';
import { BusinessShell } from './components/layout/BusinessShell';

// Auth
import LoginPage from './pages/auth/LoginPage';
import PrivacyPage from './pages/PrivacyPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import AuthCallbackPage from './pages/auth/AuthCallbackPage';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import UsersPage from './pages/admin/Users';
import BusinessesPage from './pages/admin/Businesses';
import ProductsAdminPage from './pages/admin/Products';
import OrdersPage from './pages/admin/Orders';
import { PaymentsPage, PayoutsPage, ReturnsPage } from './pages/admin/Finance';
import { RolesPage, TokenManagerPage } from './pages/admin/RolesAndTokens';
import { ChatPage, CategoriesPage, InquiriesPage } from './pages/admin/ChatAndCategories';
import InventoryGovernance from './pages/admin/Inventory';
import MarketingAndPromotions from './pages/admin/Marketing';
import SecurityAndAuditCenter from './pages/admin/Security';
import SystemConfiguration from './pages/admin/Settings';
import BannersPage from './pages/business/Banner';

// Business pages
import BusinessDashboard from './pages/business/Dashboard';
import BusinessProducts from './pages/business/Products';
import BulkUpload from './pages/business/BulkUpload';
import InventoryPage from './pages/business/Inventory';
import { BusinessOrders, BusinessPayouts, BusinessReturns } from './pages/business/OrdersFinance';
import { StaffPage, BusinessSettings } from './pages/business/StaffAndSettings';
import BusinessChat from './pages/business/Chat';
import InquiryManagement from './pages/business/Inquiry';
import BusinessCategories from './pages/business/Categories';
import BusinessReviews from './pages/business/Reviews';

import { LoadingPage } from './components/common/UI';

// ── Guards ────────────────────────────────────────────
const RequireAuth = ({ children }) => {
  const user = useSelector(selectUser);
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// ── Admin portal (13 Governed Modules) ────────────────
const AdminPortal = () => (
  <RequireAuth allowedRoles={['super_admin', 'admin', 'moderator']}>
    <AdminShell>
      <Routes>
        {/* 1. Overview */}
        <Route index element={<AdminDashboard />} />

        {/* 2. Merchants */}
        <Route path="sellers" element={<BusinessesPage />} />
        <Route path="businesses" element={<BusinessesPage />} />

        {/* 3. Catalog & Taxonomy */}
        <Route path="catalog" element={<ProductsAdminPage />} />
        <Route path="products" element={<ProductsAdminPage />} />
        <Route path="categories" element={<CategoriesPage />} />

        {/* 4. Commerce & Orders */}
        <Route path="orders" element={<OrdersPage />} />

        {/* 5. Inventory Governance */}
        <Route path="inventory" element={<InventoryGovernance />} />

        {/* 6. Finance & Double-Entry Ledger */}
        <Route path="finance" element={<PaymentsPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="payouts" element={<PayoutsPage />} />
        <Route path="returns" element={<ReturnsPage />} />

        {/* 7. Customers */}
        <Route path="customers" element={<UsersPage />} />
        <Route path="users" element={<UsersPage />} />

        {/* 8. Admin Staff & RBAC */}
        <Route path="admin-users" element={<RolesPage />} />
        <Route path="roles" element={<RolesPage />} />
        <Route path="tokens" element={<TokenManagerPage />} />

        {/* 9. Marketing & Content */}
        <Route path="marketing" element={<MarketingAndPromotions />} />

        {/* 10. Reviews Moderation */}
        <Route path="reviews" element={<BusinessReviews />} />

        {/* 11. Support Desk */}
        <Route path="support" element={<ChatPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="inquiry" element={<InquiriesPage />} />

        {/* 12. Security Center & Audits */}
        <Route path="security" element={<SecurityAndAuditCenter />} />

        {/* 13. System Configuration */}
        <Route path="settings" element={<SystemConfiguration />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminShell>
  </RequireAuth>
);

// ── Business portal ───────────────────────────────────
const BusinessPortal = () => {
  const { data: business, loading } = useQuery(() => businessAPI.mine(), []);

  if (loading) return <LoadingPage />;
  if (business?.status === 'pending_review') {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-50">
        <div className="text-center max-w-md px-6">
          <div className="text-5xl mb-4">⏳</div>
          <h2 className="font-display text-2xl font-bold text-surface-900 mb-2">
            Business Review Pending
          </h2>
          <p className="text-surface-500 text-sm">
            Aapka business abhi review mein hai। Admin approval ke baad dashboard access hoga।
          </p>
        </div>
      </div>
    );
  }
  return (
    // <RequireAuth allowedRoles={['business_owner','business_staff']}>
    <BusinessShell business={business}>
      <Routes>
        <Route index element={<BusinessDashboard business={business} />} />
        <Route path="products" element={<BusinessProducts business={business} />} />
        <Route path="upload" element={<BulkUpload business={business} />} />
        <Route path="inventory" element={<InventoryPage business={business} />} />
        <Route path="orders" element={<BusinessOrders />} />
        <Route path="payouts" element={<BusinessPayouts />} />
        <Route path="returns" element={<BusinessReturns />} />
        <Route path="reviews" element={<BusinessReviews />} />
        <Route path="categories" element={<BusinessCategories business={business} />} />
        <Route path="banners" element={<BannersPage business={business} />} />
        <Route path="staff" element={<StaffPage business={business} />} />
        <Route path="chat" element={<BusinessChat business={business} />} />
        <Route path="settings" element={<BusinessSettings business={business} />} />
        <Route path="inquiry" element={<InquiryManagement />} />

        <Route path="*" element={<Navigate to="/business" replace />} />
      </Routes>
    </BusinessShell>
    // </RequireAuth>
  );
};

const RootRedirect = () => {
  const user = useSelector(selectUser);
  if (!user) return <Navigate to="/login" replace />;
  const role = user?.role;
  if (['super_admin', 'admin', 'moderator'].includes(role)) return <Navigate to="/admin" replace />;
  if (['business_owner', 'business_staff'].includes(role)) return <Navigate to="/business" replace />;
  return <Navigate to="/business" replace />;
};
// ── App ───────────────────────────────────────────────
export default function App() {
  return (
    <Provider store={store}>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            className: 'text-sm font-medium',
            style: { borderRadius: '12px', background: '#1e293b', color: '#f8fafc', fontSize: '13px' },
            success: { iconTheme: { primary: '#22c55e', secondary: '#f8fafc' }, duration: 3000 },
            error: { iconTheme: { primary: '#ef4444', secondary: '#f8fafc' }, duration: 4000 },
          }}
        />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/privacy-policy" element={<PrivacyPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/admin/*" element={<AdminPortal />} />
          <Route path="/business/*" element={<BusinessPortal />} />
          <Route path="/unauthorized" element={
            <div className="flex h-screen items-center justify-center bg-surface-50">
              <div className="text-center">
                <p className="font-display text-4xl font-bold text-surface-900 mb-2">403</p>
                <p className="text-surface-500">You don't have permission to access this page.</p>
                <a href="/login" className="mt-4 inline-block text-sm text-brand-600 hover:underline">← Go to login</a>
              </div>
            </div>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </Provider>
  );
}